/**
 * AnimatedText Component
 *
 * Unified text animation component with multiple animation styles.
 * Provides a single interface to switch between different text animation effects.
 *
 * Animation Types:
 * - typewriter: Character by character typing effect
 * - word_reveal: Word by word with spring physics + slide up
 * - blur_fade: Word by word blur to sharp fade
 * - slide_up: Words slide up from below with stagger
 * - fade_scale: Words fade in with subtle scale
 * - bounce: Words bounce in with spring physics
 * - wave: Wave-like entrance with vertical offset
 * - flip: Words flip in from top (3D rotate)
 */

import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, spring, Easing } from 'remotion';

/** Maps text alignment to flexbox justify-content values */
const JUSTIFY_MAP = {
  center: 'center',
  right: 'flex-end',
  left: 'flex-start',
} as const;

export type TextAnimationType =
  | 'typewriter'
  | 'word_reveal'
  | 'blur_fade'
  | 'slide_up'
  | 'fade_scale'
  | 'bounce'
  | 'wave'
  | 'flip';

export interface AnimatedTextProps {
  /** Text to animate */
  text: string;
  /** Animation type to use */
  animationType?: TextAnimationType;
  /** Frame when animation should start */
  startFrame?: number;
  /** Text color */
  color?: string;
  /** Font size */
  fontSize?: number;
  /** Font weight */
  fontWeight?: number;
  /** Font family */
  fontFamily?: string;
  /** Text alignment */
  textAlign?: 'left' | 'center' | 'right';
  /** Line height */
  lineHeight?: number;
  /** Max width for text wrapping */
  maxWidth?: number;
  /** Show cursor for typewriter effect */
  showCursor?: boolean;
  /** Characters per second for typewriter */
  charsPerSecond?: number;
  /** Delay between words in frames (for word-based animations) */
  wordDelay?: number;
}

export const AnimatedText: React.FC<AnimatedTextProps> = ({
  text,
  animationType = 'typewriter',
  startFrame = 0,
  color = '#ffffff',
  fontSize = 36,
  fontWeight = 700,
  fontFamily = 'Inter, sans-serif',
  textAlign = 'center',
  lineHeight = 1.4,
  maxWidth,
  showCursor = false,
  charsPerSecond = 20,
  wordDelay = 4,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const relativeFrame = frame - startFrame;

  // Handle undefined/null text gracefully
  const safeText = text || '';

  // Common styles
  const containerStyle: React.CSSProperties = {
    color,
    fontSize,
    fontWeight,
    fontFamily,
    lineHeight,
    textAlign,
    maxWidth,
  };

  // Typewriter animation (character by character)
  if (animationType === 'typewriter') {
    const framesPerChar = fps / charsPerSecond;
    const charsToShow = Math.min(
      Math.floor(Math.max(0, relativeFrame) / framesPerChar),
      safeText.length
    );
    const visibleText = safeText.slice(0, charsToShow);
    const isTyping = charsToShow < safeText.length;

    const containerOpacity = interpolate(
      relativeFrame,
      [0, fps * 0.2],
      [0, 1],
      { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
    );

    const cursorOpacity = isTyping
      ? 1
      : (Math.floor((relativeFrame / fps) * 4) % 2 === 0 ? 1 : 0);

    return (
      <div style={{ ...containerStyle, opacity: containerOpacity }}>
        <span style={{ whiteSpace: 'pre-wrap' }}>{visibleText}</span>
        {showCursor && (
          <span style={{ opacity: cursorOpacity, marginLeft: 2 }}>|</span>
        )}
      </div>
    );
  }

  // Word-based animations
  const words = safeText.split(' ');

  const getWordStyle = (index: number): React.CSSProperties => {
    const wordStartFrame = index * wordDelay;
    const wordRelativeFrame = relativeFrame - wordStartFrame;

    // Default invisible state
    if (wordRelativeFrame < 0) {
      return {
        opacity: 0,
        display: 'inline-block',
      };
    }

    switch (animationType) {
      case 'word_reveal': {
        const springProgress = spring({
          frame: wordRelativeFrame,
          fps,
          from: 0,
          to: 1,
          durationInFrames: fps * 0.5,
          config: { stiffness: 100, damping: 15 },
        });

        const opacity = interpolate(wordRelativeFrame, [0, fps * 0.2], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });

        const translateY = interpolate(springProgress, [0, 1], [20, 0]);
        const blur = interpolate(springProgress, [0, 1], [4, 0]);

        return {
          display: 'inline-block',
          opacity,
          transform: `translateY(${translateY}px)`,
          filter: `blur(${blur}px)`,
        };
      }

      case 'blur_fade': {
        const duration = fps * 0.5;
        const opacity = interpolate(wordRelativeFrame, [0, duration], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });

        const blur = interpolate(wordRelativeFrame, [0, duration], [10, 0], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });

        return {
          display: 'inline-block',
          opacity,
          filter: `blur(${blur}px)`,
          willChange: 'opacity, filter',
        };
      }

      case 'slide_up': {
        const duration = fps * 0.4;
        const opacity = interpolate(wordRelativeFrame, [0, duration], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });

        const translateY = interpolate(
          wordRelativeFrame,
          [0, duration],
          [30, 0],
          {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
            easing: Easing.out(Easing.cubic),
          }
        );

        return {
          display: 'inline-block',
          opacity,
          transform: `translateY(${translateY}px)`,
        };
      }

      case 'fade_scale': {
        const duration = fps * 0.4;
        const opacity = interpolate(wordRelativeFrame, [0, duration], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });

        const scale = interpolate(wordRelativeFrame, [0, duration], [0.8, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
          easing: Easing.out(Easing.back(1.5)),
        });

        return {
          display: 'inline-block',
          opacity,
          transform: `scale(${scale})`,
        };
      }

      case 'bounce': {
        const springProgress = spring({
          frame: wordRelativeFrame,
          fps,
          from: 0,
          to: 1,
          durationInFrames: fps * 0.6,
          config: { stiffness: 200, damping: 12 },
        });

        const opacity = interpolate(wordRelativeFrame, [0, fps * 0.15], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });

        const scale = interpolate(springProgress, [0, 1], [0.3, 1]);
        const translateY = interpolate(springProgress, [0, 1], [-20, 0]);

        return {
          display: 'inline-block',
          opacity,
          transform: `translateY(${translateY}px) scale(${scale})`,
        };
      }

      case 'wave': {
        const duration = fps * 0.6;
        const opacity = interpolate(wordRelativeFrame, [0, duration * 0.3], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });

        // Wave effect: bouncy entrance from below with overshoot
        const waveProgress = interpolate(
          wordRelativeFrame,
          [0, duration],
          [0, 1],
          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
        );

        // Starts at 40px below, overshoots to -10px above, settles at 0
        const translateY = interpolate(
          waveProgress,
          [0, 0.4, 0.7, 1],
          [40, -12, 5, 0],
          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
        );

        return {
          display: 'inline-block',
          opacity,
          transform: `translateY(${translateY}px)`,
        };
      }

      case 'flip': {
        const duration = fps * 0.5;
        const progress = interpolate(wordRelativeFrame, [0, duration], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
          easing: Easing.out(Easing.back(1.2)),
        });

        const opacity = interpolate(wordRelativeFrame, [0, duration * 0.4], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });

        // Flip from above with stronger perspective and scale
        const rotateX = interpolate(progress, [0, 1], [-120, 0]);
        const translateY = interpolate(progress, [0, 1], [-30, 0]);
        const scale = interpolate(progress, [0, 1], [0.7, 1]);

        return {
          display: 'inline-block',
          opacity,
          transform: `perspective(200px) rotateX(${rotateX}deg) translateY(${translateY}px) scale(${scale})`,
          transformOrigin: 'center top',
        };
      }

      default:
        return { display: 'inline-block', opacity: 1 };
    }
  };

  // Container fade-in
  const containerOpacity = interpolate(
    relativeFrame,
    [0, fps * 0.2],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  return (
    <div
      style={{
        ...containerStyle,
        opacity: containerOpacity,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: JUSTIFY_MAP[textAlign],
        gap: '0.25em', // Relative to font size for proper scaling
      }}
    >
      {words.map((word, index) => (
        <span key={index} style={getWordStyle(index)}>
          {word}
        </span>
      ))}
    </div>
  );
};

export default AnimatedText;
